import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, Hash, Loader2, Paperclip, Upload } from 'lucide-react';
import { api, type CompraHoldedPreview } from '@/lib/api';
import { fmt } from '@/lib/liquidaciones';
import { classNames } from '@/lib/utils';
import CambiarNumeroFactura from './CambiarNumeroFactura';

/** Desde las liquidaciones de octubre de 2026 la compra la registra el CRM; las anteriores se metieron a mano. */
export const PRIMER_PERIODO_COMPRA = 202610;
export const aplicaCompraHolded = (liq: any) =>
  liq?.result?.tipo === 'factura' && (liq.year || 0) * 100 + (liq.month || 0) >= PRIMER_PERIODO_COMPRA;

/**
 * Paso 5 del asistente en explotación: la factura que el CRM emite en nombre
 * del propietario a Sereno. Aquí se enseña y, si el número sale mal, se
 * corrige. Desde octubre de 2026 además se registra en Holded como compra, con
 * este mismo PDF adjunto: ya no se sube a mano a la bandeja de entrada.
 */
export default function PasoFacturaPropietario({
  liquidacion,
  owner,
  esLaUltima,
  generarPdf,
  generarPdfBase64,
  onNumeroCambiado,
  onLiquidacionActualizada,
  onContinuar,
}: {
  liquidacion: any;
  owner: any;
  esLaUltima: boolean;
  /** El mismo PDF que «Generar factura» */
  generarPdf: (liq: any) => Blob;
  /** El mismo PDF, en base64, para adjuntarlo en Holded */
  generarPdfBase64: (liq: any) => Promise<{ nombre: string; base64: string }>;
  onNumeroCambiado: (r: { liquidacion: any; owner: any | null }) => void;
  onLiquidacionActualizada: (liq: any) => void;
  onContinuar: () => void;
}) {
  const [cambiando, setCambiando] = useState(false);
  const numero = liquidacion.invoiceNum != null ? `${liquidacion.invoicePrefix ?? ''}${liquidacion.invoiceNum}` : null;
  const enHolded = aplicaCompraHolded(liquidacion);
  const registrada = liquidacion.holdedPurchase || null;

  // El PDF depende del número: se regenera cada vez que cambia. La URL se crea
  // y se libera en el mismo efecto, para que nunca se use una ya liberada
  const [pdf, setPdf] = useState<{ url: string | null; error: string }>({ url: null, error: '' });
  useEffect(() => {
    if (!numero) { setPdf({ url: null, error: '' }); return; }
    let url: string | null = null;
    try {
      url = URL.createObjectURL(generarPdf(liquidacion));
      setPdf({ url, error: '' });
    } catch (err: any) {
      setPdf({ url: null, error: err?.message || 'No se pudo generar la factura' });
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [liquidacion, numero]); // eslint-disable-line react-hooks/exhaustive-deps

  const nombreArchivo = `Factura_${numero || 'sin_numero'}_${String(liquidacion.ownerName || '').replace(/\s+/g, '_')}_${String(liquidacion.periodo || '').replace(' ', '_')}.pdf`;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-sereno-dark">Factura del propietario</h2>
          <p className="text-sm text-sereno-dark/60">
            La emite el CRM en nombre de {liquidacion.ownerName} a Sereno Tech S.L.
            {enHolded ? ' y se registra en Holded como compra.' : '; las de antes de octubre de 2026 se registraron a mano en Holded.'}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono font-semibold text-sereno-dark">{numero || 'Sin número'}</p>
          {liquidacion.result?.total != null && <p className="text-sm text-sereno-dark/60">{fmt(liquidacion.result.total)}</p>}
        </div>
      </div>

      {enHolded && numero && (
        <CompraEnHolded
          liquidacion={liquidacion}
          numero={numero}
          generarPdfBase64={generarPdfBase64}
          onLiquidacionActualizada={onLiquidacionActualizada}
        />
      )}

      {!numero ? (
        <div className="flex items-start gap-2 text-sm rounded-lg border p-3 bg-amber-50 border-amber-200 text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Esta liquidación no tiene número de factura (el propietario no tenía serie al crearla). Asígnale uno para generar la factura.</span>
        </div>
      ) : pdf.error ? (
        <div className="flex items-start gap-2 text-sm rounded-lg border p-3 bg-red-50 border-red-200 text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{pdf.error}</span>
        </div>
      ) : pdf.url && (
        <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" style={{ height: '70vh' }}>
          <iframe key={pdf.url} title="Factura del propietario" src={pdf.url} className="w-full h-full" />
        </div>
      )}

      <div className="flex justify-between items-center gap-3 pt-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCambiando(true)}
            disabled={Boolean(registrada)}
            title={registrada ? 'Ya está registrada en Holded con este número' : undefined}
            className="btn-ghost text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Hash size={16} /> {numero ? 'Cambiar número' : 'Asignar número'}
          </button>
          {pdf.url && (
            <a href={pdf.url} download={nombreArchivo} className="btn-ghost text-sm flex items-center gap-2">
              <Download size={16} /> Descargar
            </a>
          )}
        </div>
        <button onClick={onContinuar} disabled={!numero} className="btn-primary flex items-center gap-2">Continuar →</button>
      </div>

      {cambiando && (
        <CambiarNumeroFactura
          liquidacion={liquidacion}
          owner={owner}
          esLaUltima={esLaUltima}
          onGuardado={r => { onNumeroCambiado(r); setCambiando(false); }}
          onCerrar={() => setCambiando(false)}
        />
      )}
    </div>
  );
}

const ORIGEN_CONTACTO: Record<string, string> = {
  ficha: 'el contacto vinculado en su ficha',
  dni: 'el contacto con su DNI/CIF',
};

function CompraEnHolded({ liquidacion, numero, generarPdfBase64, onLiquidacionActualizada }: {
  liquidacion: any;
  numero: string;
  generarPdfBase64: (liq: any) => Promise<{ nombre: string; base64: string }>;
  onLiquidacionActualizada: (liq: any) => void;
}) {
  const [preview, setPreview] = useState<CompraHoldedPreview | null>(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [error, setError] = useState('');
  const [avisos, setAvisos] = useState<string[]>([]);
  const registrada = liquidacion.holdedPurchase || (preview?.aplica && preview.creada) || null;

  useEffect(() => {
    if (liquidacion.holdedPurchase) { setCargando(false); return; }
    let vivo = true;
    setCargando(true);
    setError('');
    api.getCompraHolded(liquidacion.id)
      .then(p => { if (vivo) setPreview(p); })
      .catch(err => { if (vivo) setError(err?.message || 'No se pudo preparar la compra'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [liquidacion.id, numero]); // eslint-disable-line react-hooks/exhaustive-deps

  async function registrar() {
    setTrabajando(true);
    setError('');
    try {
      const r = await api.registrarCompraHolded(liquidacion.id, await generarPdfBase64(liquidacion));
      onLiquidacionActualizada(r.liquidacion);
      setAvisos(r.avisos || []);
    } catch (err: any) {
      setError(err?.message || 'No se pudo registrar la compra en Holded');
    } finally {
      setTrabajando(false);
      setConfirmar(false);
    }
  }

  async function completar() {
    setTrabajando(true);
    setError('');
    try {
      const pdf = registrada?.adjunto ? undefined : await generarPdfBase64(liquidacion);
      const r = await api.completarCompraHolded(liquidacion.id, pdf);
      onLiquidacionActualizada(r.liquidacion);
      setAvisos(r.avisos || []);
    } catch (err: any) {
      setError(err?.message || 'No se pudo completar la compra en Holded');
    } finally {
      setTrabajando(false);
    }
  }

  if (cargando) {
    return (
      <div className="flex items-center gap-2 text-sm text-sereno-dark/60 rounded-xl border border-gray-200 p-4">
        <Loader2 className="animate-spin" size={16} /> Preparando la compra en Holded…
      </div>
    );
  }

  if (registrada) {
    const falta = !registrada.adjunto || registrada.draft;
    return (
      <div className="space-y-2">
        <div className={classNames('flex items-start gap-3 rounded-xl border p-4', falta ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50')}>
          {falta ? <AlertTriangle className="text-amber-600 shrink-0" size={22} /> : <CheckCircle2 className="text-green-600 shrink-0" size={22} />}
          <div className="text-sm flex-1 space-y-0.5">
            <p className={classNames('font-semibold', falta ? 'text-amber-800' : 'text-green-800')}>
              Registrada en Holded como la compra {registrada.numero || numero}
              {registrada.cuenta === 'test' ? ' (cuenta de pruebas)' : ''}
            </p>
            <p className={falta ? 'text-amber-800/80' : 'text-green-700/70'}>
              {registrada.total != null ? `${fmt(registrada.total)} · ` : ''}
              {registrada.draft ? 'en borrador, sin aprobar' : 'aprobada'}
            </p>
            <p className={classNames('flex items-center gap-1.5', falta ? 'text-amber-800/80' : 'text-green-700/70')}>
              <Paperclip size={13} />
              {registrada.adjunto ? <>Factura adjunta <span className="font-mono text-xs">({registrada.adjunto.nombre})</span></> : 'Sin la factura en PDF adjunta'}
            </p>
          </div>
          {falta && (
            <button onClick={completar} disabled={trabajando} className="btn-ghost text-sm flex items-center gap-2 whitespace-nowrap">
              {trabajando ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              {!registrada.adjunto ? 'Adjuntar factura' : 'Aprobar'}
            </button>
          )}
        </div>
        {avisos.map((a, i) => <Aviso key={i} tono="error">{a}</Aviso>)}
        {error && <Aviso tono="error">{error}</Aviso>}
      </div>
    );
  }

  if (!preview) return error ? <Aviso tono="error">{error}</Aviso> : null;
  if (!preview.aplica) return <Aviso tono="info">{preview.motivo}</Aviso>;
  if (preview.creada) return null; // ya cubierto arriba con `registrada`

  const p = preview;
  const fecha = new Date(`${p.fecha}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const proveedor = p.contacto.origen
    ? `${p.contacto.nombreEnHolded || p.contacto.nombre}, ${ORIGEN_CONTACTO[p.contacto.origen] || 'en Holded'}`
    : `${p.contacto.nombre}: no está en Holded y se dará de alta como proveedor con su DNI/CIF`;
  const bloqueo = p.bloqueo || (p.duplicada
    ? `Ya existe en Holded la compra ${p.duplicada.numero} de este propietario: no se registra otra. Si es esta misma factura, se metió a mano.`
    : null);

  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-semibold text-sereno-dark">Compra en Holded</p>
        {p.cuenta === 'test' && (
          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-100">
            Cuenta de pruebas · DEMO SERENO TECH
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
        <p><span className="text-sereno-dark/60">Proveedor:</span> {proveedor}</p>
        <p><span className="text-sereno-dark/60">Número y fecha:</span> <span className="font-mono">{p.numero}</span> · {fecha}</p>
        <p className="sm:col-span-2"><span className="text-sereno-dark/60">Concepto:</span> {p.concepto}</p>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 tabular-nums">
        <div><dt className="text-xs text-sereno-dark/60">Base</dt><dd>{fmt(p.base)}</dd></div>
        <div><dt className="text-xs text-sereno-dark/60">IVA 21%</dt><dd>{fmt(p.iva)}</dd></div>
        <div><dt className="text-xs text-sereno-dark/60">Retención 19%</dt><dd>{p.irpf ? `−${fmt(p.irpf)}` : '—'}</dd></div>
        <div><dt className="text-xs text-sereno-dark/60">Total</dt><dd className="font-semibold">{fmt(p.total)}</dd></div>
      </dl>
      {!bloqueo && p.diferencia !== 0 && (
        <p className="text-xs text-amber-700">La factura dice {fmt(p.totalFactura)}: {fmt(Math.abs(p.diferencia))} de diferencia por el redondeo de Holded.</p>
      )}
      {bloqueo && <Aviso tono="error">{bloqueo}</Aviso>}
      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex justify-end">
        <button onClick={() => setConfirmar(true)} disabled={Boolean(bloqueo) || trabajando} className="btn-primary flex items-center gap-2">
          <Upload size={16} /> Registrar en Holded
        </button>
      </div>

      {confirmar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
            <h3 className="text-lg font-bold text-sereno-dark">Registrar la compra</h3>
            <p className="text-sm text-sereno-dark/70">
              Se creará en Holded{p.cuenta === 'test' ? ' (cuenta de pruebas)' : ''} la compra <strong>{p.numero}</strong> por{' '}
              <strong>{fmt(p.total)}</strong>, con esta factura en PDF adjunta, y se aprobará.
              {' '}No la subas también a la bandeja de entrada de Holded: saldría duplicada.
            </p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setConfirmar(false)} disabled={trabajando} className="btn-ghost flex-1">Cancelar</button>
              <button onClick={registrar} disabled={trabajando} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {trabajando ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {trabajando ? 'Registrando…' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Aviso({ tono, children }: { tono: 'info' | 'error'; children: React.ReactNode }) {
  return (
    <div className={classNames(
      'flex items-start gap-2 text-sm rounded-lg border p-3',
      tono === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800',
    )}>
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
